import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import type { Analysis, AnalysisKind, Sentiment, AnalysisSignal } from "@/lib/types";
import { useApi } from "@/hooks/useApi";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { fmtRelativeTime, fmtPrice } from "@/lib/format";
import { Sparkles, RefreshCw } from "lucide-react";

const SENTIMENT: Record<Sentiment, { label: string; variant: "success" | "secondary" | "danger" }> = {
  bullish: { label: "Positiv", variant: "success" },
  neutral: { label: "Neutral", variant: "secondary" },
  bearish: { label: "Negativ", variant: "danger" },
};
const SIGNAL: Record<AnalysisSignal, { label: string; variant: "success" | "secondary" | "danger" }> = {
  buy: { label: "Köp", variant: "success" },
  hold: { label: "Behåll", variant: "secondary" },
  sell: { label: "Sälj", variant: "danger" },
};
const KIND_LABEL: Record<AnalysisKind, string> = {
  premarket: "Förmarknad",
  intraday: "Intradag",
  longterm: "Långsiktig",
};

type Tab = "short" | "long";
const TABS: { id: Tab; label: string; runKind: AnalysisKind }[] = [
  { id: "short", label: "Kortsiktig", runKind: "intraday" },
  { id: "long", label: "Långsiktig", runKind: "longterm" },
];

type Latest = Record<AnalysisKind, Analysis | null>;

/** Newest of the two short-term scopes. */
function newestShort(latest: Latest): Analysis | null {
  const both = [latest.premarket, latest.intraday].filter(Boolean) as Analysis[];
  return both.sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;
}

function AnalysisView({ a }: { a: Analysis }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{KIND_LABEL[a.kind]}</Badge>
        <Badge variant={SENTIMENT[a.sentiment].variant}>{SENTIMENT[a.sentiment].label}</Badge>
        <Badge variant={SIGNAL[a.signal].variant}>Signal: {SIGNAL[a.signal].label}</Badge>
        <Badge variant="outline">Säkerhet {Math.round(a.confidence * 100)}%</Badge>
      </div>
      <p className="text-sm leading-relaxed">{a.summary}</p>
      <ul className="space-y-1 text-sm text-muted-foreground">
        {a.keyPoints.map((p, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-primary">•</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="text-xs text-muted-foreground">
        {a.trigger === "manual" ? "Manuell" : "Schemalagd"} · {a.model} · {fmtRelativeTime(a.createdAt)}
        {a.priceAtRun != null ? ` · vid ${fmtPrice(a.priceAtRun)}` : ""}
      </div>
    </div>
  );
}

export function AiAnalysisPanel() {
  const { data, loading, refetch } = useApi<{ latest: Latest }>("/api/analysis");
  const [tab, setTab] = useState<Tab>("short");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Remember the shared secret so it's only typed once per browser.
  const [password, setPassword] = useLocalStorage<string>("analysisPassword", "");

  const runKind = TABS.find((t) => t.id === tab)!.runKind;
  const latest = data?.latest;
  const current = latest ? (tab === "short" ? newestShort(latest) : latest.longterm) : null;

  // POST the run request for the active tab's scope.
  const post = (pw: string) =>
    fetch("/api/analysis/run", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(pw ? { "x-analysis-password": pw } : {}) },
      body: JSON.stringify({ kind: runKind }),
    });

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      let pw = password;
      let res = await post(pw);

      // 401 = gate enabled and our (possibly empty/stale) password was rejected.
      // Prompt for it and retry once.
      if (res.status === 401) {
        const entered = window.prompt("Lösenord för att köra analys:") ?? "";
        if (!entered) {
          setError("Lösenord krävs.");
          return;
        }
        pw = entered;
        res = await post(pw);
      }

      const body = await res.json();
      if (!res.ok) {
        if (res.status === 401) setPassword(""); // forget the bad password
        throw new Error(body?.error ?? `HTTP ${res.status}`);
      }
      setPassword(pw); // remember the working password
      refetch();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="size-4" /> AI-analys
        </CardTitle>
        <Button size="sm" variant="outline" onClick={run} disabled={running}>
          <RefreshCw className={running ? "size-3.5 animate-spin" : "size-3.5"} />
          {running ? "Analyserar…" : `Kör ${TABS.find((t) => t.id === tab)!.label.toLowerCase()}`}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="mb-4 inline-flex rounded-lg bg-muted p-0.5">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`rounded-md px-3 py-1 text-sm transition-colors ${
                tab === t.id ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && <p className="mb-3 text-sm text-red-500">{error}</p>}
        {loading && !data ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-16" />
          </div>
        ) : current ? (
          <AnalysisView a={current} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Ingen {tab === "short" ? "kortsiktig" : "långsiktig"} analys än. Klicka på "Kör" (kräver OPENAI_API_KEY i
            .env).
          </p>
        )}
        <Separator className="my-4" />
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          Detta är inte finansiell rådgivning. AI-analysen är heuristisk och baseras på pris, tekniska indikatorer och
          nyheter.
        </p>
      </CardContent>
    </Card>
  );
}
